/**
 * Settings Service - API calls for user app settings
 *
 * PURPOSE:
 * Provides a clean interface for user settings API operations.
 * Settings are simple key-value pairs stored as JSONB in the database.
 *
 * PATTERN:
 * - Returns `data | null` (null on any error)
 * - Uses logger for error reporting
 * - No hydration needed (settings are stored/returned as-is)
 *
 * API ENDPOINTS:
 * - GET    /api/settings           → Get all user settings
 * - GET    /api/settings/:key      → Get specific setting
 * - PUT    /api/settings/:key      → Create or update setting
 * - DELETE /api/settings/:key      → Delete setting
 *
 * COMMON SETTING KEYS:
 * - `bond_interest_rate_defaults` - Default interest rate settings for new bonds
 * - `display_preferences` - UI preferences (theme, etc.)
 */

import { logger } from '~/services/logging';
import type { BondInterestRateSettings } from '~/types/app-settings';

// ============================================
// Types
// ============================================

/** All user settings as a key-value map */
export type SettingsMap = Record<string, unknown>;

/** Known setting keys for type safety */
export type KnownSettingKey = 'bond_interest_rate_defaults' | 'display_preferences';

/** Type-safe setting values */
export interface KnownSettings {
  bond_interest_rate_defaults: BondInterestRateSettings;
  display_preferences: {
    theme?: 'light' | 'dark' | 'system';
    compactMode?: boolean;
  };
}

// ============================================
// API Methods
// ============================================

/**
 * Get all settings for the current user
 * Returns a map of setting keys to values
 */
export async function getAllSettings(): Promise<SettingsMap | null> {
  try {
    const response = await fetch('/api/settings');

    if (!response.ok) {
      logger.L2.debug('[SettingsService] Failed to fetch settings', {
        context: 'api:settings',
        status: response.status,
      });
      return null;
    }

    const { settings } = (await response.json()) as { settings: SettingsMap };
    return settings;
  } catch (error) {
    logger.L2.debug('[SettingsService] Error fetching settings', {
      context: 'api:settings',
      error: String(error),
    });
    return null;
  }
}

/**
 * Get a specific setting by key
 * Returns the setting value or null if not found
 */
export async function getSetting<K extends KnownSettingKey>(
  key: K
): Promise<KnownSettings[K] | null>;
export async function getSetting(key: string): Promise<unknown | null>;
export async function getSetting(key: string): Promise<unknown | null> {
  try {
    const response = await fetch(`/api/settings/${key}`);

    if (!response.ok) {
      if (response.status === 404) {
        // Setting not found is a normal case (not an error)
        return null;
      }
      logger.L2.debug('[SettingsService] Failed to fetch setting', {
        context: 'api:settings',
        key,
        status: response.status,
      });
      return null;
    }

    const { value } = (await response.json()) as { key: string; value: unknown };
    return value;
  } catch (error) {
    logger.L2.debug('[SettingsService] Error fetching setting', {
      context: 'api:settings',
      key,
      error: String(error),
    });
    return null;
  }
}

/**
 * Create or update a setting
 * Returns the saved value on success, null on failure
 */
export async function setSetting<K extends KnownSettingKey>(
  key: K,
  value: KnownSettings[K]
): Promise<KnownSettings[K] | null>;
export async function setSetting(key: string, value: unknown): Promise<unknown | null>;
export async function setSetting(key: string, value: unknown): Promise<unknown | null> {
  try {
    const response = await fetch(`/api/settings/${key}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ value }),
    });

    if (!response.ok) {
      logger.L2.debug('[SettingsService] Failed to save setting', {
        context: 'api:settings',
        key,
        status: response.status,
      });
      return null;
    }

    const result = (await response.json()) as { key: string; value: unknown };
    return result.value;
  } catch (error) {
    logger.L2.debug('[SettingsService] Error saving setting', {
      context: 'api:settings',
      key,
      error: String(error),
    });
    return null;
  }
}

/**
 * Delete a setting
 * Returns true on success, false on failure
 */
export async function deleteSetting(key: string): Promise<boolean> {
  try {
    const response = await fetch(`/api/settings/${key}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      if (response.status === 404) {
        // Setting not found - consider this a success (idempotent delete)
        return true;
      }
      logger.L2.debug('[SettingsService] Failed to delete setting', {
        context: 'api:settings',
        key,
        status: response.status,
      });
      return false;
    }

    return true;
  } catch (error) {
    logger.L2.debug('[SettingsService] Error deleting setting', {
      context: 'api:settings',
      key,
      error: String(error),
    });
    return false;
  }
}

// ============================================
// Convenience Methods
// ============================================

/**
 * Get bond interest rate defaults for the current user
 * Falls back to app defaults if not set
 */
export async function getBondInterestRateDefaults(): Promise<BondInterestRateSettings | null> {
  const settings = await getSetting('bond_interest_rate_defaults');
  return settings as BondInterestRateSettings | null;
}

/**
 * Save bond interest rate defaults for the current user
 */
export async function setBondInterestRateDefaults(
  settings: BondInterestRateSettings
): Promise<BondInterestRateSettings | null> {
  return setSetting('bond_interest_rate_defaults', settings);
}

/**
 * Get or create bond interest rate defaults
 * Returns user defaults if set, otherwise returns app defaults
 */
export async function getOrCreateBondInterestRateDefaults(): Promise<BondInterestRateSettings> {
  const { DEFAULT_APP_SETTINGS } = await import('~/types/app-settings');

  const userDefaults = await getBondInterestRateDefaults();
  return userDefaults || DEFAULT_APP_SETTINGS.bondInterestRates;
}

// ============================================
// Export as namespace-like object (optional)
// ============================================

export const SettingsService = {
  getAllSettings,
  getSetting,
  setSetting,
  deleteSetting,
  getBondInterestRateDefaults,
  setBondInterestRateDefaults,
  getOrCreateBondInterestRateDefaults,
};
